import { ShieldCheckIcon } from 'lucide-react'
import { useState } from 'react'

import { PageBody, PageHeader } from '@/components/AppShell'
import { EmptyState, ErrorState, TableSkeleton } from '@/components/states'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'

import { revokeTeamMember, setTeamTier, useStaffMutation, useStaffTeam, type TeamMember } from '../api'
import { useStaff } from '../hooks'
import { ActionNotice, ReasonDialog, StaffGate } from '../ui'
import { SELECT_CLASS } from '../shared'
import { STAFF_TIERS } from '../tier'

type Pending = { member: TeamMember; kind: 'tier' | 'revoke' }

export function StaffTeamPage() {
  const staff = useStaff()
  const allowed = staff.can('staff_roles')
  const team = useStaffTeam(allowed && staff.mfa)
  const [pending, setPending] = useState<Pending | null>(null)
  const [tier, setTier] = useState('support')
  const [notice, setNotice] = useState<{ title: string; notified: boolean | undefined } | null>(null)
  const setTierMutation = useStaffMutation(({ id, tier: t, reason }: { id: string; tier: string; reason: string }) => setTeamTier(id, t, reason))
  const revoke = useStaffMutation(({ id, reason }: { id: string; reason: string }) => revokeTeamMember(id, reason))

  return (
    <StaffGate needs="staff_roles">
      <PageHeader
        eyebrow="Staff"
        title="Team"
        description="Who holds a staff tier. A tier change replaces the previous one; you cannot change your own, nor demote the last superadmin."
      />
      <PageBody>
        <ActionNotice notice={notice} onDismiss={() => { setNotice(null) }} />
        <Card>
          <CardContent className="space-y-4">
            {team.isError ? (
              <ErrorState error={team.error} title="Could not load the team" onRetry={() => void team.refetch()} />
            ) : team.isPending ? (
              <TableSkeleton rows={4} columns={4} />
            ) : team.data.length === 0 ? (
              <EmptyState icon={ShieldCheckIcon} title="No staff" description="Nobody holds a staff role." />
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Tier</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {team.data.map((m) => (
                      <TableRow key={m.email} data-testid="staff-team-row">
                        <TableCell className="max-w-[18rem] truncate font-mono text-xs">{m.email}</TableCell>
                        <TableCell>{m.name ?? '—'}</TableCell>
                        <TableCell><Badge variant="outline">{m.tier}</Badge></TableCell>
                        <TableCell>
                            {m.user_id === null ? (
                              <span className="text-xs text-muted-foreground">no saltseed user</span>
                            ) : (
                              <div className="flex justify-end gap-2">
                                <Button size="sm" variant="outline" onClick={() => { setTier(m.tier); setPending({ member: m, kind: 'tier' }) }}>
                                  Change tier
                                </Button>
                                <Button size="sm" variant="destructive" onClick={() => { setPending({ member: m, kind: 'revoke' }) }}>
                                  Revoke
                                </Button>
                              </div>
                            )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </PageBody>

      {pending === null || pending.member.user_id === null ? null : pending.kind === 'tier' ? (
        <ReasonDialog
          title={`Change ${pending.member.email}’s tier`}
          description="Replaces their current staff tier. The change is audited with your reason."
          confirmLabel="Set tier"
          onClose={() => { setPending(null) }}
          onConfirm={async (reason) => {
            const id = pending.member.user_id ?? ''
            await setTierMutation.mutateAsync({ id, tier, reason })
            setNotice({ title: `${pending.member.email} is now ${tier}`, notified: undefined })
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="team-tier">New tier</Label>
            <select id="team-tier" className={SELECT_CLASS} value={tier} onChange={(e) => { setTier(e.target.value) }}>
              {STAFF_TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </ReasonDialog>
      ) : (
        <ReasonDialog
          title={`Revoke ${pending.member.email}?`}
          description="Removes every staff role. They keep their ordinary account. The change is audited with your reason."
          confirmLabel="Revoke staff access"
          destructive
          onClose={() => { setPending(null) }}
          onConfirm={async (reason) => {
            await revoke.mutateAsync({ id: pending.member.user_id ?? '', reason })
            setNotice({ title: `${pending.member.email} is no longer staff`, notified: undefined })
          }}
        />
      )}
    </StaffGate>
  )
}
